import React, {useEffect, useState} from 'react'
import {Stack, Typography} from '@mui/material'
import {colors} from '@/constants'
import HoverButton from '@/components/UI/button/HoverButton'
import {getBaseUrl} from '@/helpers/GetBaseUrl'
import UserService from '@/services/api/User'
import StatusDashboardPaper from '@/components/Modals/Profile/Dashboard/StatusDashboardPaper'
import {AxiosResponseDashboardInterface} from '@/types/types'
import SimpleLoader from '@/components/UI/simpleLoader'
import useFetching from '@/hooks/useFetching'
import QuestionTooltip from '@/components/UI/questionTooltip'
import {errorCodeHandler} from '@/helpers/localErrorCodeHandler'
import TransCustom from '@/components/UI/i18next'

const Index = () => {
  const [dashboard, setDashboard] = useState<AxiosResponseDashboardInterface>({
    advisor_count: 0,
    hexatiles_on_sale: 0,
    owned_hexatiles: 0,
    purchased_for_all_time: 0,
    terra_coins: 0,
    user_owner_status: '',
    promotion_status: '',
    octa_coef: 0,
    steps: {'0': 10},
  })
  const [isLoadLocal, setIsLoadLocal] = useState<boolean>(true)
  const [steps, setSteps] = useState<
    {checked: boolean; value: number; name: string; nextStep: boolean}[]
  >([])
  const [end, setEnd] = useState<number>(0)
  const [start, setStart] = useState(0)
  const [sum, setSum] = useState<number>(0)

  const [getDashboard, isLoading] = useFetching(async () => {
    try {
      const response = await UserService.getDashboard()
      if (response.status === 200) {
        const values = Object.values(response.data.steps)
        const firstValue = values[0]
        const lastValue = values[values.length - 1]
        const newList = Object.entries(response.data.steps)
          .map(([key, value], index) => {
            if (index === 0) {
              return null
            }

            return {
              checked: value < response.data.owned_hexatiles,
              value: value,
              name: 'Step ' + (index - 1),
              nextStep: false,
            }
          })
          .filter(
            (
              item
            ): item is {checked: boolean; value: number; name: string; nextStep: boolean} =>
              item !== null
          )

        if (newList.length > 0) {
          newList[newList.length - 1].name = response.data.promotion_status
        }

        const nextStepIndex = newList.findIndex(
          (result) => result.value > response.data.owned_hexatiles
        )
        if (nextStepIndex !== -1 && newList[nextStepIndex]) {
          newList[nextStepIndex].nextStep = true
        }

        setEnd(lastValue)
        setStart(firstValue)
        setSteps(newList)

        setSum(lastValue - firstValue)
        response.data.terra_coins = Math.floor(
          response.data.purchased_for_all_time / 50
        )
        setDashboard(response.data)
      }
    } catch (e) {
      errorCodeHandler(e)
    }
  })

  useEffect(() => {
    if (isLoading) {
      setIsLoadLocal(true)
    } else {
      const timer = setTimeout(() => {
        setIsLoadLocal(false)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [isLoading])

  useEffect(() => {
    void getDashboard()
  }, [])

  return isLoadLocal ? (
    <Stack height="100%" padding="20px">
      <SimpleLoader isLoad={isLoadLocal} local={true} />
    </Stack>
  ) : (
    <Stack
      style={{wordBreak: 'break-word'}}
      width="80%"
      height="100%"
      justifyContent="space-between"
      className="events-block"
      paddingRight="20px"
      gap="20px">
      <TransCustom i18nKey="1247" variant="h1" color={colors.yellow} />
      <Stack>
        <Stack
          borderBottom="1px solid #FFFDF033"
          justifyContent="space-between"
          direction="row"
          padding="20px 0">
          <Typography variant="body1">Owned hexatiles</Typography>
          <Typography variant="body1" style={{color: colors.orange}}>
            {dashboard.owned_hexatiles}
          </Typography>
        </Stack>
        <Stack
          borderBottom="1px solid #FFFDF033"
          justifyContent="space-between"
          direction="row"
          padding="20px 0">
          <Typography variant="body1">Hexatiles ON SALE</Typography>
          <Typography variant="body1" style={{color: colors.orange}}>
            {dashboard.hexatiles_on_sale}
          </Typography>
        </Stack>
        <Stack
          borderBottom="1px solid #FFFDF033"
          justifyContent="space-between"
          direction="row"
          padding="20px 0">
          <TransCustom i18nKey="1383" variant="body1" />
          <Typography variant="body1" style={{color: colors.orange}}>
            {dashboard.purchased_for_all_time}
          </Typography>
        </Stack>
        <Stack
          borderBottom="1px solid #FFFDF033"
          justifyContent="space-between"
          direction="row"
          padding="20px 0">
          <Typography variant="body1" position="relative">
            <TransCustom i18nKey="1384" />
            <QuestionTooltip
              isClick
              id="dashboard-2"
              styles={{maxWidth: '500px'}}
              top="-2px">
              <Stack gap="10px" marginTop="10px">
                <TransCustom i18nKey="1385" />
              </Stack>
            </QuestionTooltip>
          </Typography>
          <Typography variant="body1" style={{color: colors.orange}}>
            {dashboard.terra_coins}
          </Typography>
        </Stack>
        <Stack justifyContent="space-between" direction="row" padding="20px 0">
          <Typography variant="body1">Advisors</Typography>
          <Typography variant="body1" style={{color: colors.orange}}>
            {dashboard.advisor_count}
          </Typography>
        </Stack>
      </Stack>
      <Stack gap="70px">
        <Stack direction="row" gap="40px">
          <Stack>
            <Stack direction="row" color="white" alignItems="center">
              <TransCustom i18nKey="1386" />:
              <Typography
                variant="button"
                style={{marginLeft: '5px', color: colors.orange}}>
                {dashboard.user_owner_status}
              </Typography>
              <QuestionTooltip id="dashboard-1" styles={{maxWidth: '500px'}}>
                <Stack gap="10px">
                  <TransCustom i18nKey="1387" />
                </Stack>
              </QuestionTooltip>
            </Stack>
            <Typography variant="body2" color={colors.gray}>
              ( {end - dashboard.owned_hexatiles} Hexatiles for the next status
              )
            </Typography>
          </Stack>
          <Typography
            width="fit-content"
            variant="button"
            style={{textDecoration: 'none'}}
            position="relative">
            Octa coin:
            <span style={{marginLeft: '5px', color: colors.orange}}>
              +{dashboard.octa_coef ? dashboard.octa_coef : 0}%
            </span>
            <QuestionTooltip id="dashboard-3" styles={{maxWidth: '500px'}}>
              <Stack gap="10px">
                <TransCustom i18nKey="1388" />
              </Stack>
            </QuestionTooltip>
          </Typography>
        </Stack>
        <Stack position="relative" marginBottom="10px">
          <Stack
            width="90%"
            boxShadow="0px 0px 10px 0px #FFF17233"
            border="1px solid #FFD7AE"
            borderRadius="20px"
            height="11px"
            style={{background: '#0F0A00'}}>
            <Stack
              position="absolute"
              width={`${((dashboard.owned_hexatiles - start) / (end - start)) * 90}%`}
              height="11px"
              style={{background: colors.yellow}}
            />
          </Stack>
          {steps.map((step, i) => (
            <StatusDashboardPaper
              start={dashboard.steps[1]}
              key={'step' + i}
              step={step}
              sum={sum}
              owned={dashboard.owned_hexatiles}
            />
          ))}
        </Stack>
        <HoverButton
          fontSize="16px"
          fontWeight="500"
          width="277px"
          height="38px"
          onClick={() => {
            window.open(getBaseUrl() + '/leaderboard', '_blank')
          }}
          textColor={colors.white}
          boxShadowHover="0px 0px 10px 0px #FFF17266 inset"
          padding="10px 20px">
          <TransCustom i18nKey="12481" />
        </HoverButton>
      </Stack>
    </Stack>
  )
}

export default Index
